import {Component, CUSTOM_ELEMENTS_SCHEMA, inject, Inject, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {DeliveryService} from '../../shared/service/delivery/delivery.service';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {combineLatest, iif, Observable, of, ReplaySubject, Subject, Subscription, switchMap, tap} from 'rxjs';
import {ErrorInfo} from '../../shared/model/error-info';
import {DeliveryDescription} from '../../shared/model/delivery-description';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {TranslateService, TranslatePipe} from '@ngx-translate/core';
import {Delivery} from '../../shared/model/delivery';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {DatePipe, NgClass, AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {Platform} from '@angular/cdk/platform';
import {DeliveryProduct} from '../../shared/model/delivery-product';
import {PageState, PageStateService} from '../../shared/service/page-state/page-state.service';
import {AppStateService, DocumentStoreService, TargetBlankDirective} from 'commons';
import {PersonalService} from '../../shared/service/personal/personal.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {MatCard} from '@angular/material/card';
import {ChangeCategoriesComponent} from '../change-categories/change-categories.component';
import {
  DacklAccordionComponent,
  DacklExpansionPanelComponent,
  DacklEmptyStateComponent,
  DacklBreakpointObserver, DacklBreakpoint
} from '@datev/ngx-dackl';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatFabButton} from '@angular/material/button';
import {MatSlideToggle, MatSlideToggleChange} from '@angular/material/slide-toggle';
import {FormsModule} from '@angular/forms';
import '@datev/dacks/content';
import '@datev/dacks/content-right-aside';
import '@datev/doc-viewer';
import {HtmlViewerComponent} from '../html-viewer/html-viewer.component';
import {DeliveryProductsTableViewComponent} from '../delivery-products-table-view/delivery-products-table-view.component';
import { FooterSpacing } from "../footer/footer-spacing";


export interface DeliveryMainProductsViewModel {
  description: DeliveryDescription;
  productGroups: string[];
  groupedProducts: Map<string, DeliveryProduct[]>;
}

export interface DeliveryMainProductsPageState extends PageState {
  expandedPanelIds: string[];
  isPersonalFilter: boolean;
}

@Component({
  selector: 'myupdates-delivery-main-products',
  templateUrl: './delivery-main-products.component.html',
  styleUrls: ['./delivery-main-products.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatButton, MatIcon, DacklAccordionComponent, DacklExpansionPanelComponent, ChangeCategoriesComponent, RouterLink,
    NgClass, MatCard, DacklEmptyStateComponent, ErrorBoxComponent, MatProgressSpinner, AsyncPipe, TranslatePipe, NgTemplateOutlet,
    MatSlideToggle, FormsModule, DatePipe, TargetBlankDirective, MatFabButton,
    HtmlViewerComponent, DeliveryProductsTableViewComponent, FooterSpacing]
})
export class DeliveryMainProductsComponent implements OnInit, OnDestroy {

  @ViewChildren(DeliveryProductsTableViewComponent) tableViews?: QueryList<DeliveryProductsTableViewComponent>;

  isLegalNoticeShown = true;
  pdfAdditionalInfoText = '';

  breakpoint$ = inject(DacklBreakpointObserver).breakpoint$;
  isLargeScreenWithRightAside$ = this.breakpoint$.pipe(map(breakpoint => breakpoint >= DacklBreakpoint.LG));
  isLargeScreenWithRightAside = false;

  selectedProduct: DeliveryProduct | null = null;

  detailsContentError$ = new Subject<boolean>();
  documentBuffer$?: Observable<{ buffer: ArrayBuffer; fileName: string } | null>;

  constructor(private readonly route: ActivatedRoute,
              private readonly router: Router,
              private readonly deliveryService: DeliveryService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly adobeTrackingService: AdobeTrackingService,
              private readonly translate: TranslateService,
              readonly appState: AppStateService,
              private readonly personalService: PersonalService,
              private readonly datePipe: DatePipe,
              private readonly pageStateService: PageStateService,
              private readonly documentStoreService: DocumentStoreService,
              public platform: Platform,
              @Inject('Window') private readonly window: Window) {
  }

  delivery$?: Observable<DeliveryMainProductsViewModel>;
  errorInfo$ = new ReplaySubject<ErrorInfo>(1);
  private personalizeSubscription?: Subscription;

  pageState = {} as DeliveryMainProductsPageState;

  ngOnInit(): void {
    this.pdfAdditionalInfoText = this.translate.instant('common.pdf_additional_info');
    this.isLargeScreenWithRightAside$.subscribe(isLargeScreenWithRightAside => {
      this.isLargeScreenWithRightAside = isLargeScreenWithRightAside;
      if (!isLargeScreenWithRightAside) {
        this.clearSelection();
      }
    });

    this.pageState = this.pageStateService.pageState as DeliveryMainProductsPageState;
    if (!this.pageState.expandedPanelIds) {
      this.pageState.expandedPanelIds = [];
    }
    if (this.pageState.isPersonalFilter !== undefined) {
      this.appState.isPersonalDeliveryFilter = this.pageState.isPersonalFilter;
    } else {
      this.pageState.isPersonalFilter = this.appState.isPersonalDeliveryFilter;
    }

    const routeParams = this.route.snapshot.paramMap;

    const productsDeliveryId = routeParams.get('id');

    if (productsDeliveryId) {

      this.personalizeSubscription = this.appState.isPersonalized$.pipe(
        switchMap(isPersonalized =>
          iif(() => isPersonalized,
            combineLatest([this.personalService.activeBusinessPartner$, this.appState.isPersonalDeliveryFilter$],
              (_, isPersonalFilterActive) => isPersonalFilterActive),
            of(false))
        ),
        // Fehleranzeige: Zugriff auf Personalisierungsdaten
        catchError(err => {
          this.errorInfo$.next(this.errorHandlingService.toErrorInfo(err));
          return of();
        })
      ).subscribe(isPersonalizedView => {
        this.delivery$ = this.deliveryService.getDeliveryById(productsDeliveryId, isPersonalizedView, true).pipe(
          tap(delivery => this.validateDeliveryProductsDateInFuture(delivery.description.deliveryDate)),
          map(delivery => this.buildViewModel(delivery)),
          // Fehleranzeige: Zugriff auf Seitendaten (+ personalisierte Filter)
          catchError(err => {
            this.errorInfo$.next(this.errorHandlingService.toErrorInfo(err));
            return of();
          })
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.personalizeSubscription?.unsubscribe();
  }

  buildViewModel(delivery: Delivery): DeliveryMainProductsViewModel {
    const viewModel = delivery.products.reduce((storage, item) => {
        const group = item.productGroup ?? this.translate.instant('delivery-products.no_product_group');

        if (storage.groupedProducts.has(group)) {
          storage.groupedProducts.get(group)?.push(item);
        } else {
          storage.groupedProducts.set(group, [ item ]);
          storage.productGroups.push(group);
        }

        return storage;
      },
      { description: delivery.description,
        groupedProducts: new Map<string, DeliveryProduct[]>(),
        productGroups: [] } as DeliveryMainProductsViewModel);

    viewModel.productGroups.sort((a: string, b: string) => a.localeCompare(b));

    return viewModel;
  }

  selectProduct(product: DeliveryProduct): void {
    if (product.documentId || product.documentUrl) {
      this.selectedProduct = product;
      if (product.documentUrl) {
        this.documentBuffer$ = this.documentStoreService.getPdfBuffer(product.documentUrl).pipe(
          tap(() => this.detailsContentError$.next(false)),
          catchError(() => {
            this.detailsContentError$.next(true);
            return of(null);
          }),
          map(buffer => buffer ? { buffer, fileName: `${crypto.randomUUID()}.pdf` } : null)
        );
      }
    }
  }

  private clearSelection(): void {
    this.selectedProduct = null;
    this.documentBuffer$ = undefined;
  }

  onRightAsideCloseClick(): void {
    const productToFocus = this.selectedProduct;
    this.clearSelection();

    if (productToFocus?.id && this.tableViews) {
      setTimeout(() => {
        this.tableViews?.forEach(view =>
          view.focusProductLink(productToFocus.id)
        );
      });
    }
  }

  navigateToProductDetails(product: DeliveryProduct, event?: KeyboardEvent): void {
    if (!event || event.code === 'Space' || event.code === 'Enter') {
      if ((product?.documentId || product?.documentUrl) && product?.id) {
        void this.router.navigate(['product', product.id], {relativeTo: this.route});
      }
    }
  }

  shareByMail(deliveryDescription: DeliveryDescription | null): void {

    if (!deliveryDescription) {
      return;
    }

    // Adobe Analytics Einbindung in erster Runde
    this.adobeTrackingService.publishClickEvent('Update-Termine:Ein Termin:Teilen');

    const formattedDate = this.datePipe.transform(deliveryDescription.deliveryDate, 'dd.MM.yyyy');
    const mailSubject = `Leseempfehlung \\ Überblick Programme ${deliveryDescription.deliveryName} ${formattedDate} \\ DATEV MyUpdates`;
    const mailBody = `Ich möchte Ihnen den folgenden Beitrag in DATEV MyUpdates empfehlen:` +
      `%0A${environment.entryPoint}delivery/main-products/${deliveryDescription.id}%0A%0A` +
      `Mit freundlichem Gruß`;

    this.window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`;
  }

  onPanelOpened(panelId: string): void {
    this.pageState.expandedPanelIds.push(panelId);
  }

  onPanelClosed(panelId: string): void {
    const ix = this.pageState.expandedPanelIds.indexOf(panelId);
    if (ix >= 0) {
      this.pageState.expandedPanelIds.splice(ix, 1);
    }
  }

  // Methode zum Anzeigen des Hinweises "Es kann zu Änderungen kommen"
  validateDeliveryProductsDateInFuture(deliveryDate: Date): void {
    this.isLegalNoticeShown = DeliveryService.validateDateIsInFuture(deliveryDate);
  }

  changePersonalFilterState(event: MatSlideToggleChange): void {
    const isPersonalFilter = event.checked;
    this.appState.isPersonalDeliveryFilter = isPersonalFilter;
    this.adobeTrackingService.publishClickEvent('UpdateTermineHauptrelease:PersonalFilter:Toggle' +
    (isPersonalFilter ? 'Meine' : 'Alle'));
    this.pageState.isPersonalFilter = isPersonalFilter;
  }

}
